import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import cloneDeep from 'lodash/cloneDeep';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import capitalize from 'lodash/capitalize';
import { http } from '@pendo/http';
import moment, { DATE_FORMAT, convertToSubscriptionTimezone } from '@/utils/moment';

export function getHistory (guideId, params) {
    return http.get(`/api/s/_SID_/guide/${guideId}/history`, { params }).then((res) => res.data);
}

export function convertGuideHistoryToSessions (history, timezone) {
    let lastUser;
    let lastDate;

    return history.reduce((acc, curr) => {
        curr.revisionDateFormatted = convertToSubscriptionTimezone(timezone, curr.lastUpdatedAt).format(
            DATE_FORMAT.short
        );

        if (curr.revisionDateFormatted === lastDate && curr.lastUpdatedByUser.username === lastUser) {
            return acc;
        }

        lastUser = curr.lastUpdatedByUser.username;
        lastDate = curr.revisionDateFormatted;
        acc.push(curr);

        return acc;
    }, []);
}

export function formatRevisionDateTimestamp (timestamp, activeTimezone) {
    const timeInSubscriptionTimezone = convertToSubscriptionTimezone(activeTimezone, timestamp);

    return `${timeInSubscriptionTimezone.format(DATE_FORMAT.short)} - ${timeInSubscriptionTimezone.format(
        DATE_FORMAT.fullTime
    )}`;
}

export function stepHasVisualChanges (selectedStep, previousStep) {
    if (!selectedStep || !previousStep) {
        return true;
    }
    if (selectedStep.buildingBlocksUrl !== previousStep.buildingBlocksUrl) {
        return true;
    }

    return false;
}

export function stepHasNonVisualChanges (selectedStep, previousStep) {
    if (!selectedStep || !previousStep) {
        return true;
    }
    if (get(selectedStep, 'attributes.isAutoFocus') !== get(previousStep, 'attributes.isAutoFocus')) {
        return true;
    }
    if (selectedStep.elementPathRule !== previousStep.elementPathRule) {
        return true;
    }
    if (selectedStep.featureId !== previousStep.featureId) {
        return true;
    }
    if (selectedStep.name !== previousStep.name) {
        return true;
    }
    if (selectedStep.pageId !== previousStep.pageId) {
        return true;
    }

    return false;
}

export function filterRevisionsByUser (revisions = [], users = []) {
    if (!users.length) return revisions;

    const userMap = users.reduce((map, user) => {
        const userId = user.id || user;
        map[userId] = true;

        return map;
    }, {});

    return revisions.filter((revision) => {
        const { username } = revision.lastUpdatedByUser;

        return userMap[username];
    });
}

export const oneDay = 24 * 60 * 60 * 1000;

export function filterRevisionsByDate (revisions, start, end) {
    if (!start || !end) return revisions;
    const startValue = moment(start).valueOf();
    const endValue = moment(end).valueOf() + oneDay;

    return revisions.filter((revision) => {
        return revision.lastUpdatedAt >= startValue && revision.lastUpdatedAt < endValue;
    });
}

export function getDefaultStartDate (guide) {
    const ninetyDays = oneDay * 90;
    const startDate = moment(guide.lastUpdatedAt - ninetyDays).format(DATE_FORMAT.iso);

    return moment(startDate).valueOf();
}

export function areRevisionsEqual (revision, otherRevision) {
    return (
        otherRevision.lastUpdatedAt === revision.lastUpdatedAt &&
        otherRevision.lastUpdatedByUser.username === revision.lastUpdatedByUser.username
    );
}

export const GUIDE_AUDIT_PROPS_TO_CHECK = Object.freeze({
    NAME: 'NAME',
    LAUNCH_METHOD: 'LAUNCH_METHOD',
    AUDIENCE: 'AUDIENCE',
    SHOWS_AFTER: 'SHOWS_AFTER',
    EXPIRES_AFTER: 'EXPIRES_AFTER',
    STATE: 'STATE',
    DESCRIPTION: 'DESCRIPTION',
    DEVICE_TYPE: 'DEVICE_TYPE',
    OVERRIDE_AUTO_THROTTLING: 'OVERRIDE_AUTO_THROTTLING',
    LOCALIZATION: 'LOCALIZATION',
    REDISPLAY: 'REDISPLAY',
    DELIVERY_FREQUENCY: 'DELIVERY_FREQUENCY',
    EMAIL_CONFIG: 'EMAIL_CONFIG',
    MOBILE_DISPLAY: 'MOBILE_DISPLAY'
});

const GUIDE_AUDIT_PROP_PATH = Object.freeze({
    [GUIDE_AUDIT_PROPS_TO_CHECK.NAME]: 'name',
    [GUIDE_AUDIT_PROPS_TO_CHECK.LAUNCH_METHOD]: 'launchMethod',
    [GUIDE_AUDIT_PROPS_TO_CHECK.AUDIENCE]: 'audienceUiHint.filters[0].segmentId',
    [GUIDE_AUDIT_PROPS_TO_CHECK.REDISPLAY]: 'redisplay',
    [GUIDE_AUDIT_PROPS_TO_CHECK.DELIVERY_FREQUENCY]: 'recurrence',
    [GUIDE_AUDIT_PROPS_TO_CHECK.LOCALIZATION]: 'translationStates',
    [GUIDE_AUDIT_PROPS_TO_CHECK.SHOWS_AFTER]: 'showsAfter',
    [GUIDE_AUDIT_PROPS_TO_CHECK.EXPIRES_AFTER]: 'expiresAfter',
    [GUIDE_AUDIT_PROPS_TO_CHECK.STATE]: 'state',
    [GUIDE_AUDIT_PROPS_TO_CHECK.DESCRIPTION]: 'description',
    [GUIDE_AUDIT_PROPS_TO_CHECK.DEVICE_TYPE]: 'attributes.device.type',
    [GUIDE_AUDIT_PROPS_TO_CHECK.OVERRIDE_AUTO_THROTTLING]: 'attributes.overrideAutoThrottling',
    [GUIDE_AUDIT_PROPS_TO_CHECK.EMAIL_CONFIG]: 'emailConfiguration',
    [GUIDE_AUDIT_PROPS_TO_CHECK.MOBILE_DISPLAY]: 'attributes.mobileDisplaySettings'
});

export function getStatus (guide, checkOnLastUpdated = false) {
    const isPublicToo = isScheduled(guide, checkOnLastUpdated) && guide.state === 'public';
    const state = isPublicToo ? 'scheduled' : guide.state;

    return {
        label: capitalize(state),
        status: state
    };
}

export function isScheduled (guide, checkOnLastUpdated = false) {
    if (!guide?.showsAfter) return false;

    if (checkOnLastUpdated) {
        return guide.showsAfter > guide.lastUpdatedAt;
    }

    return guide.showsAfter > Date.now();
}

const SHOWS_AFTER_PREFIX = 'Start Date / Time:\n';
const EXPIRES_AFTER_PREFIX = 'Expiration Date / Time:\n';

export function getPropDefaultValue (prop) {
    switch (prop) {
        case GUIDE_AUDIT_PROPS_TO_CHECK.AUDIENCE:
            return 'Everyone';
        case GUIDE_AUDIT_PROPS_TO_CHECK.OVERRIDE_AUTO_THROTTLING:
            return false;
        case GUIDE_AUDIT_PROPS_TO_CHECK.DESCRIPTION:
        case GUIDE_AUDIT_PROPS_TO_CHECK.REDISPLAY:
        case GUIDE_AUDIT_PROPS_TO_CHECK.MOBILE_DISPLAY:
            return '--';
        case GUIDE_AUDIT_PROPS_TO_CHECK.EXPIRES_AFTER:
            return `${EXPIRES_AFTER_PREFIX}---`;
        case GUIDE_AUDIT_PROPS_TO_CHECK.SHOWS_AFTER:
            return `${SHOWS_AFTER_PREFIX}---`;
        case GUIDE_AUDIT_PROPS_TO_CHECK.DELIVERY_FREQUENCY:
            return 'One time';
        default:
            return '';
    }
}

export function launchMethodToStringVDS (guide, options = {}) {
    const { launchMethod } = guide;
    const isMobileApp = get(options, 'isMobileApp', false);
    const isConfirmation = guide?.attributes?.badge?.isConfirmation;

    if (!launchMethod) return '';

    const launchMethodNames = launchMethod.split('-').map((method) => {
        switch (method) {
            case 'auto':
                if (isMobileApp) return 'App launch';

                return 'Automatic';
            case 'badge':
                if (isConfirmation) return 'Confirmation';

                return 'Badge';
            case 'launcher':
                return 'Resource Center';
            case 'page':
                return 'Page view';
            case 'feature':
                return 'Feature click';
            case 'track':
                return 'Track event';
            case 'dom':
                return 'Target element';
            default:
                return '';
        }
    });

    return launchMethodNames.join(', ');
}

function getDeviceLabel (value) {
    switch (value) {
        case 'desktop':
        case 'Desktop':
            return 'Desktop & tablets only';
        case 'Mobile':
        case 'mobile':
            return 'Mobile only';
        default:
            return 'All devices';
    }
}

export function getPropValue (revision, prop, options = {}) {
    if (!Object.values(GUIDE_AUDIT_PROPS_TO_CHECK).includes(prop)) return null;

    const isMobileApp = get(options, 'isMobileApp', false);
    if (GUIDE_AUDIT_PROPS_TO_CHECK.LAUNCH_METHOD === prop) return launchMethodToStringVDS(revision, { isMobileApp });
    if (GUIDE_AUDIT_PROPS_TO_CHECK.STATE === prop) return getStatus(revision, true);

    const getThrottling = (isActive) => (isActive ? 'Has throttling enabled' : 'Ignores throttling');
    const activeTimezone = get(options, 'activeTimezone', 'America/New_York');
    const datePrefix = prop === GUIDE_AUDIT_PROPS_TO_CHECK.EXPIRES_AFTER ? EXPIRES_AFTER_PREFIX : SHOWS_AFTER_PREFIX;
    let value = get(revision, GUIDE_AUDIT_PROP_PATH[prop]);

    switch (prop) {
        case GUIDE_AUDIT_PROPS_TO_CHECK.DEVICE_TYPE:
            return getDeviceLabel(value);
        case GUIDE_AUDIT_PROPS_TO_CHECK.OVERRIDE_AUTO_THROTTLING:
            return getThrottling(value);
        case GUIDE_AUDIT_PROPS_TO_CHECK.LOCALIZATION:
            return getTranslationNames(value, options.languageMapping);
        case GUIDE_AUDIT_PROPS_TO_CHECK.EXPIRES_AFTER:
        case GUIDE_AUDIT_PROPS_TO_CHECK.SHOWS_AFTER:
            if (!isNumber(value)) {
                value = false;
                break;
            }

            return `${datePrefix}${convertToSubscriptionTimezone(activeTimezone, value).format(DATE_FORMAT.full)}`;
    }

    if (!value) return getPropDefaultValue(prop);

    if (isString(value)) {
        value = value.trim();
    }

    if (!value) return getPropDefaultValue(prop);

    return value;
}

export function getTranslationNames (guideTranslations, languageMapping) {
    const translationsWithNames = [];
    if (!guideTranslations) return translationsWithNames;

    for (const code of Object.keys(guideTranslations)) {
        translationsWithNames.push({
            code,
            name: languageMapping[code].name
        });
    }

    return translationsWithNames;
}

export function formatSelectedLocalizations (prev, selected) {
    const previousMap = keyBy(cloneDeep(prev), 'code');
    const added = [];
    for (const translation of selected) {
        if (previousMap[translation.code]) {
            delete previousMap[translation.code];
        } else {
            added.push(translation);
        }
    }

    const removed = Object.values(previousMap);

    const formattedSelected = {
        all: selected,
        ...(added.length > 0 ? { added } : {}),
        ...(removed.length > 0 ? { removed } : {})
    };

    return formattedSelected;
}

export const ONE_HOUR = 60 * 60 * 1000;

export const ONE_DAY = ONE_HOUR * 60 * 60 * 1000;

export function formatRedisplay (prev, curr) {
    return [prev, curr].map((revision) => {
        if (revision === getPropDefaultValue(GUIDE_AUDIT_PROPS_TO_CHECK.REDISPLAY)) return revision;

        const rawInterval = revision.interval;
        const unit = revision.timeUnit;
        const formattedIntervalTime = rawInterval / (unit === 'Day' ? ONE_DAY : ONE_HOUR);
        const formattedUnit = formattedIntervalTime > 1 ? `${unit}s` : unit;
        const formattedIntervalString =
            formattedIntervalTime > 1
                ? `every ${formattedIntervalTime} ${formattedUnit.toLowerCase()}`
                : `every ${formattedUnit.toLowerCase()}`;
        const dismissalString =
            revision.maxDismissals > 0 ? `Expires after ${revision.maxDismissals} dismissals` : 'Never expires';

        return `Repeat ${formattedIntervalString}. ${dismissalString}.`;
    });
}

export function formatFrequency (prev, curr) {
    return [prev, curr].map((revision) => {
        if (revision === getPropDefaultValue(GUIDE_AUDIT_PROPS_TO_CHECK.DELIVERY_FREQUENCY)) return revision;

        const formattedInterval = revision / ONE_DAY;

        return formattedInterval > 1 ? `Every ${formattedInterval} days` : 'Every day';
    });
}

export function formatMobileDisplay (prev, curr) {
    return [prev, curr].map((revision) => {
        if (revision === getPropDefaultValue(GUIDE_AUDIT_PROPS_TO_CHECK.MOBILE_DISPLAY)) return revision;

        return `${revision.deviceType}, ${revision.orientation}`;
    });
}
